import {api} from "@/shared/api";
import * as Input from "@/shared/utils/input";
import {FieldObserver} from "@/shared/ui/fieldObserver";
import {Combobox} from "@/shared/ui/combobox";
import {FormUtil} from "@/shared/forms/formUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {fillOvertimeDeductionAccountOrder, mergeProperties} from "@/shared/utils/misc";
import {DOMUtil} from "@/shared/utils/dom";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	formatDate,
	formatDuration,
	formatVacation,
	getCurrentISOOffset,
	getISODate,
	parseISODate,
	parseVacation,
	Temporal
} from "@/shared/utils/datefunctions";
import {ErrorResponse, TimeAccountTableRow, User, WorkScheduleModel} from "@/shared/timr";
import {OvertimeDeductionAccountType, TimeAccountEntryType, VacationUnit, WorkScheduleModelType} from "@/shared/timrTypes";
import {promiseNow} from "@/shared/utils/promise";

export enum InitTimeAccountUserMode {
	Existing = "existing",
	New = "new"
}

export class TimeAccountInitializationDialog {

	public static EVENT_INITIALIZED = "eventInitialized";

	private modal: BootstrapModal.Modal;
	private command: TimeAccountInitializationCommand;
	private observer: FieldObserver<TimeAccountInitializationCommand>;
	private wizard: JQuery;
	private username: string;
	private workScheduleModelComboBox: Combobox<WorkScheduleModel>;
	private customOvertimeDeductionAccountOrder: OvertimeDeductionAccountType[];
	private editMode = false;
	private editTimeAccountId: number | null = null;

	constructor() {
		this.modal = new BootstrapModal.Modal({
			title: "user.crud.timeAccount.initialization",
			message: $("#userTimeAccountInitializationDialog"),
			footer: false,
			css: {
				content: "test-css"
			},
			onOpen: () => {
				this.updateFieldValues();
				this.initDeductFromAccountSelects(this.customOvertimeDeductionAccountOrder);
			},
			onClose: () => {
				this.editMode = false;
				this.editTimeAccountId = null;
				this.customOvertimeDeductionAccountOrder = null;
				this.wizard.find("a[href='#finish']").text(messages["admin.timeaccount.init"]);
				this.resetWizard();
			}
		});

		this.initWizard();
		this.initWorkScheduleModelComboBox();
		this.initListener();
	}

	private initWizard() {
		this.wizard = $("#userTimeAccountInitializationDialog > form").steps({
			headerTag: "h6",
			bodyTag: "fieldset",
			transitionEffect: "fade",
			cssClass: "wizard modal-wizard",
			enableCancelButton: true,
			enableKeyNavigation: false,
			labels: {
				previous: messages["steps.previous"],
				next: messages["steps.next"],
				finish: messages["admin.timeaccount.init"],
				cancel: messages["dialog.cancel"]
			},
			onStepChanging: (event: Event, currentIndex: number, newIndex: number) => {
				this.updateTitle();

				const nextButton = this.wizard.find("a[href='#next']").parent();
				Input.toggle(nextButton, newIndex !== 1 || this.workScheduleModelComboBox?.getItems()?.length > 0);

				return this.command.workingTimeTracking || this.command.vacationAccount;
			},
			onFinished: (event: Event) => {
				const form = $(event.target);
				const button = form.find(".actions > ul > li > a[href='#finish']");

				this.initializeTimeaccount(this.modal, button);
			},
			onInit: (event: Event) => {
				// Initialize custom input components in wizard
				const form = $(event.target);
				Input.initSingleDateRangePickers(form.find(".daterange-single"));
			},
			onCanceled: () => {
				this.modal.close();
			}
		});
	}

	private resetWizard() {
		this.wizard.steps("reset");
	}

	private initListener() {
		$("input[type=radio][name=newUserTypeTimeAccount]").change((event) => {
			this.command.timeAccountEntryType = $(event.currentTarget).val() as TimeAccountEntryType;
			if (!this.editMode) {
				this.command.initializationDate = getISODate(this.getDefaultInitializationDate(this.command.timeAccountEntryType));
				this.updateFieldValues();
			}
			this.switchInitializationFields();
		});

		const numberOrNull = (sel: JQuery.Selector) => {
			const value = $(sel).val() as string;
			return value === "null" ? null : Number(value);
		};

		const parseVacationFromField = (sel: JQuery.Selector) => {
			const value = $(sel).val() as string;
			return parseVacation(value, this.command.vacationUnit);
		};

		this.observer = new FieldObserver();

		this.observer.observeInput("#userTimeAccountInitializationTrustBasedWorkingTime", "trustBasedWorkingTime");
		this.observer.observeInput("#userTimeAccountInitializationWorkingTimeTracking", "workingTimeTracking");
		this.observer.observeInput("#userTimeAccountInitializationVacationAccount", "vacationAccount");

		this.observer.observeInput("#userTimeAccountInitializationOvertimeAllowanceAllIn", "overtimeAllowanceAllIn");
		this.observer.observeInput("#userTimeAccountInitializationOvertimeDurationIncluded", "overtimeDurationIncluded", FieldObserver.getDurationFromField);
		this.observer.observeInput("#userTimeAccountInitializationAllowClearNegativeWorkDurationBalance", "allowClearNegativeWorkDurationBalance", FieldObserver.getCheckboxState);
		this.observer.observeInput("#userTimeAccountInitializationAllInSetWorkDurationBalanceZero", "allInSetWorkDurationBalanceZero", FieldObserver.getCheckboxState);

		this.observer.observeInput("#userTimeAccountInitializationWorkDuration", "initializationWorkDuration", FieldObserver.getDurationFromField);

		this.observer.observeInput("#userTimeAccountInitializationVacationUnit", "vacationUnit");
		this.observer.observeInput("#userTimeAccountInitializationVacationYearStart", "vacationYearStart", FieldObserver.getDateJsonFromField);

		this.observer.observeInput("#userTimeAccountInitializationVacation", "initializationVacation", parseVacationFromField);
		this.observer.observeInput("#userTimeAccountInitializationFirstVacation", "initializationVacation", parseVacationFromField);

		this.observer.observeInput("#userTimeAccountInitializationDate", "initializationDate", FieldObserver.getDateJsonFromField);
		this.observer.observeInput("#userTimeAccountInitializationFirstDay", "initializationDate", FieldObserver.getDateJsonFromField);

		this.observer.observeInput("#userTimeAccountInitializationVacationDaysPerYear", "vacationDaysPerYear", FieldObserver.getDecimalNumberFromField);

		this.observer.observeInput("#userTimeAccountInitializationHolidayCalendar", "holidayCalendarId", numberOrNull);

		this.observer.onFieldValueChange(((event, data) => {
			this.updateFieldValues();

			if (data.field === "workingTimeTracking") {
				this.toggleFeatureWarning();
				this.toggleWorkingTimeTrackingFields();
			}
			if (data.field === "vacationAccount") {
				this.toggleFeatureWarning();
				this.toggleVacationAccountFields();
			}
		}));
	}

	private initWorkScheduleModelComboBox() {
		this.workScheduleModelComboBox = new Combobox<WorkScheduleModel>({
			items: [],
			formatter: (wsm: WorkScheduleModel) => wsm.name,
			idElement: $("#userTimeAccountInitializationWorkScheduleModelId"),
			filterElement: $("#userTimeAccountInitializationWorkScheduleModelFilter"),
			caretElement: $("#userTimeAccountInitializationWorkScheduleModelCaret"),
			clearable: false
		});
		$(this.workScheduleModelComboBox).on(Combobox.EVENT_CHANGED, (_e, wsm?: WorkScheduleModel) => this.onWorkScheduleModelChange(wsm));
	}

	private onWorkScheduleModelChange(wsm?: WorkScheduleModel) {
		const isAlternatingModel = wsm?.workScheduleModelType === WorkScheduleModelType.ALTERNATING_WEEKS;
		$("#userTimeAccountInitializationWorkScheduleModelAlternatingStartWeekContainer").toggle(isAlternatingModel);
		if (isAlternatingModel) {
			// fill alternating start week select
			DOMUtil.fillSelect(
				$("#userTimeAccountInitializationWorkScheduleModelAlternatingStartWeek"),
				wsm?.workScheduleModelWeeks?.sort((a, b) => a.weekNumber - b.weekNumber),
				false,
				(wsmw) => DOMUtil.createOption(String(wsmw.weekNumber), wsmw.name)
			);
		}
	}

	public showForUser(user: User, timeAccountEntryType: TimeAccountEntryType) {
		this.username = user.fullname;

		const overtimeDurationIncluded = user.overtimeDurationIncluded ?? 0;

		const initializationDate = this.getDefaultInitializationDate(timeAccountEntryType);
		const defaultVacationYearStart = Temporal.Now.plainDateISO().with({month: 1, day: 1});

		this.command = {
			timeAccountEntryType: timeAccountEntryType,
			userId: user.id,
			trustBasedWorkingTime: false,
			workingTimeTracking: true,
			vacationAccount: SecurityStatus.isVacationAccountAllowed(),
			workScheduleModelId: user.workScheduleModelId,
			workScheduleModelAlternatingStartWeek: null,
			vacationUnit: user.vacationUnit,
			holidayCalendarId: user.holidayCalendarId,
			overtimeAllowanceAllIn: user.overtimeAllowanceAllIn,
			overtimeDeductionAccountOrder: user.overtimeDeductionAccountOrder,
			vacationDaysPerYear: user.vacationDaysPerYear || 0,
			overtimeDurationIncluded: overtimeDurationIncluded,
			allowClearNegativeWorkDurationBalance: user.allowClearNegativeWorkDurationBalance,
			allInSetWorkDurationBalanceZero: user.allInSetWorkDurationBalanceZero,
			vacationYearStart: user.vacationYearStart || getISODate(defaultVacationYearStart),
			initializationDate: getISODate(initializationDate),
			initializationVacation: 0,
			initializationWorkDuration: 0,
			timeZone: getCurrentISOOffset()
		};

		this.updateTimeAccountEntryTypeFields();
		this.toggleWorkingTimeTrackingFields();
		this.toggleVacationAccountFields();
		this.updateTitle();

		this.observer.setModel(this.command);

		$.when(this.loadWorkScheduleModels(), this.loadHolidayCalendars())
			.done(() => {
				this.modal.show();
			});
	}

	// Entry and re-entry propose the first working day, initialization proposes the balance date at the end of the previous month
	private getDefaultInitializationDate(timeAccountEntryType: TimeAccountEntryType): Temporal.PlainDate {
		const firstDayOfMonth = Temporal.Now.plainDateISO().with({day: 1});
		return timeAccountEntryType === TimeAccountEntryType.Initialization
			? firstDayOfMonth.subtract({days: 1})
			: firstDayOfMonth;
	}

	public showForEdit(timeAccount: TimeAccountTableRow) {
		this.username = timeAccount.userFullname ?? "";
		this.editMode = true;
		this.editTimeAccountId = timeAccount.id;

		this.customOvertimeDeductionAccountOrder = timeAccount.userOvertimeDeductionAccountOrder;

		this.command = {
			timeAccountEntryType: timeAccount.timeAccountEntryType,
			userId: timeAccount.userId,
			trustBasedWorkingTime: timeAccount.trustBasedWorkingTime,
			workingTimeTracking: timeAccount.workingTimeTracking,
			vacationAccount: timeAccount.vacationAccount,
			workScheduleModelId: timeAccount.userWorkScheduleModelId,
			workScheduleModelAlternatingStartWeek: timeAccount.workScheduleModelAlternatingStartWeek ?? null,
			vacationUnit: timeAccount.vacationUnit,
			holidayCalendarId: timeAccount.userHolidayCalendarId,
			overtimeAllowanceAllIn: timeAccount.overtimeAllowanceAllIn ?? false,
			overtimeDeductionAccountOrder: timeAccount.userOvertimeDeductionAccountOrder ?? [],
			vacationDaysPerYear: timeAccount.userVacationDaysPerYear ?? 0,
			overtimeDurationIncluded: timeAccount.overtimeDurationIncluded ?? 0,
			allowClearNegativeWorkDurationBalance: timeAccount.userAllowClearNegativeWorkDurationBalance ?? false,
			allInSetWorkDurationBalanceZero: timeAccount.userAllInSetWorkDurationBalanceZero ?? false,
			vacationYearStart: timeAccount.userVacationYearStart,
			initializationDate: timeAccount.date,
			initializationVacation: timeAccount.vacationTotalBalance ?? 0,
			initializationWorkDuration: timeAccount.workDurationTotalBalance ?? 0,
			timeZone: getCurrentISOOffset()
		};

		this.updateTimeAccountEntryTypeFields();
		this.toggleWorkingTimeTrackingFields();
		this.toggleVacationAccountFields();
		this.observer.setModel(this.command);

		$.when(this.loadWorkScheduleModels(), this.loadHolidayCalendars())
			.done(() => {
				// Pre-select the alternating start week that was in use at initialization time.
				// loadWorkScheduleModels() has already populated the select via onWorkScheduleModelChange.
				if (this.command.workScheduleModelAlternatingStartWeek != null) {
					$("#userTimeAccountInitializationWorkScheduleModelAlternatingStartWeek")
						.val(String(this.command.workScheduleModelAlternatingStartWeek));
				}
				this.modal.show();
				this.updateTitle();
				this.wizard.find("a[href='#finish']").text(messages["dialog.saveChanges"]);
			});
	}

	private updateFieldValues() {
		$("#userTimeAccountInitializationDialog").find(".vacationUnitLabel").text(messages["vacationUnit." + this.command.vacationUnit]);
		$("#userTimeAccountInitializationHolidayCalendar").val(this.command.holidayCalendarId);
		$("#userTimeAccountInitializationVacationUnit").val(this.command.vacationUnit);
		$("#userTimeAccountInitializationTrustBasedWorkingTime").prop("checked", this.command.trustBasedWorkingTime);
		$("#userTimeAccountInitializationWorkingTimeTracking").prop("checked", this.command.workingTimeTracking);
		$("#userTimeAccountInitializationVacationAccount").prop("checked", this.command.vacationAccount);
		$("#userTimeAccountInitializationVacationDaysPerYear").val(this.command.vacationDaysPerYear);
		$("#userTimeAccountInitializationOvertimeAllowanceAllIn").prop("checked", this.command.overtimeAllowanceAllIn);

		$("#userTimeAccountInitializationDate").val(formatDate(parseISODate(this.command.initializationDate)));
		$("#userTimeAccountInitializationVacationYearStart").val(formatDate(parseISODate(this.command.vacationYearStart)));

		$("#userTimeAccountInitializationWorkDuration").val(formatDuration(this.command.initializationWorkDuration));
		$("#userTimeAccountInitializationVacation").val(formatVacation(this.command.initializationVacation, this.command.vacationUnit));

		$("#userTimeAccountInitializationFirstDay").val(formatDate(parseISODate(this.command.initializationDate)));

		$("#userTimeAccountInitializationFirstVacation").val(formatVacation(this.command.initializationVacation, this.command.vacationUnit));

		const showOvertimeIncludedOptions = this.command.overtimeAllowanceAllIn || this.command.overtimeDurationIncluded > 0;
		$("#userTimeAccountInitializationAllowClearNegativeWorkDurationBalance").prop("checked", this.command.allowClearNegativeWorkDurationBalance);
		$("#userTimeAccountInitializationAllowClearNegativeWorkDurationBalanceContainer").slideToggleBool(showOvertimeIncludedOptions);
		$("#userTimeAccountInitializationAllInSetWorkDurationBalanceZero").prop("checked", this.command.allInSetWorkDurationBalanceZero);
		$("#userTimeAccountInitializationAllInSetWorkDurationBalanceZeroContainer").slideToggleBool(this.command.overtimeAllowanceAllIn);
		$("#userTimeAccountInitializationOvertimeAllowanceInfo").slideToggleBool(showOvertimeIncludedOptions);
		$("#userTimeAccountInitializationDeductFromAccountContainer").slideToggleBool(showOvertimeIncludedOptions);

		const overtimeAllowanceField = $("#userTimeAccountInitializationOvertimeDurationIncluded");
		overtimeAllowanceField.prop("disabled", this.command.overtimeAllowanceAllIn);

		if (!this.command.overtimeAllowanceAllIn && this.command.overtimeDurationIncluded !== undefined) {
			overtimeAllowanceField.val(formatDuration(this.command.overtimeDurationIncluded));
		} else {
			overtimeAllowanceField.val("");
		}

	}

	private loadWorkScheduleModels(): JQuery.jqXHR<WorkScheduleModel[]> {
		return api("admin/findWorkScheduleModelsForSelect.json")
			.get()
			.done((data: WorkScheduleModel[]) => {
				const noWsmInfo = $("#userTimeAccountInitializationWorkScheduleModelEmptyInfo");
				if (data.length > 0) {
					noWsmInfo.hide();
					this.workScheduleModelComboBox.updateItems(
						data,
						data.find(wsm => wsm.id === this.command.workScheduleModelId),
						true
					);
				} else {
					noWsmInfo.show();
				}
				this.onWorkScheduleModelChange(this.workScheduleModelComboBox.getSelectedItem());
			});
	}

	private loadHolidayCalendars(): JQueryPromise<unknown> {
		const holidayCalendarSelect = $("#userTimeAccountInitializationHolidayCalendar");
		if (holidayCalendarSelect.length > 0) {
			return api("admin/holidayCalendars.json")
				.get()
				.done(data => {
					holidayCalendarSelect.empty();

					data.holidayCalendars.forEach(holidayCalendar => {
						const optionElement = DOMUtil.createOption(String(holidayCalendar.id), holidayCalendar.description);
						holidayCalendarSelect.append(optionElement);
					});

					// Select first entry if no calendar is specified
					if (this.command.holidayCalendarId == null && data.holidayCalendars[0]) {
						this.command.holidayCalendarId = data.holidayCalendars[0].id;
					}
				});
		} else {
			return promiseNow();
		}
	}

	// INITIALIZATION_LEGACY has no radio button of its own and is shown as INITIALIZATION
	private get selectedTimeAccountEntryType(): TimeAccountEntryType {
		return this.command.timeAccountEntryType === TimeAccountEntryType.InitializationLegacy
			? TimeAccountEntryType.Initialization
			: this.command.timeAccountEntryType;
	}

	private updateTimeAccountEntryTypeFields() {
		const selected = this.selectedTimeAccountEntryType;
		const isReEntry = selected === TimeAccountEntryType.ReEntry;

		const radios = $(`input[type="radio"][name="newUserTypeTimeAccount"]`);
		radios.filter(`[value="${selected}"]`).prop("checked", true);
		radios.prop("disabled", this.editMode || isReEntry);
		$(`.radio:has(input[type="radio"][name="newUserTypeTimeAccount"][value="RE_ENTRY"])`).toggle(isReEntry);
		$(`.radio:has(input[type="radio"][name="newUserTypeTimeAccount"]:not([value="RE_ENTRY"]))`).toggle(!isReEntry);

		this.switchInitializationFields();
	}

	private switchInitializationFields() {
		const newUserTimeAccount = this.command.timeAccountEntryType === TimeAccountEntryType.UserEntry;
		const reEntryTimeAccount = this.command.timeAccountEntryType === TimeAccountEntryType.ReEntry;
		$("#userTimeAccountInitializationForNew").toggle(newUserTimeAccount);
		$("#userTimeAccountInitializationForExisting").toggle(!newUserTimeAccount);
		$("#userTimeAccountInitializationDateBalanceLabel, #userTimeAccountInitializationBalanceHint").toggle(!reEntryTimeAccount);
		$("#userTimeAccountInitializationDateFirstDayLabel, #userTimeAccountInitializationFirstDayHint").toggle(reEntryTimeAccount);
	}

	private toggleWorkingTimeTrackingFields() {
		this.wizard.find(".workingTimeTracking-field").toggle(this.command.workingTimeTracking);
	}

	private toggleVacationAccountFields() {
		this.wizard.find(".vacationAccount-field").toggle(this.command.vacationAccount);
	}

	private toggleFeatureWarning() {
		const showWarning = !this.command.workingTimeTracking && !this.command.vacationAccount;

		this.wizard.find("#activateFeatureWarning").toggle(showWarning);
		this.wizard.find("a[href='#next']").closest("li")
			.prop("aria-disabled", String(showWarning))
			.toggleClass("disabled", showWarning);
	}

	private initializeTimeaccount(modal: BootstrapModal.Modal, button: JQuery) {
		// Set the work schedule model id at the end to ensure that the correct value is used
		// override the value from the command with the selected value from the combobox
		const data: TimeAccountInitializationCommand = mergeProperties(this.command, {
			workScheduleModelId: this.workScheduleModelComboBox.getSelectedItem().id,
			workScheduleModelAlternatingStartWeek: Number($("#userTimeAccountInitializationWorkScheduleModelAlternatingStartWeek").val() as string)
		});

		// DEVNOTE: Merging is not possible for this type (as duplicates can be created in the array),
		// so we have to set the array manually (TIMR-8274)
		data.overtimeDeductionAccountOrder = this.customOvertimeDeductionAccountOrder;

		const form = $("#userTimeAccountInitializationDialog > form");
		const request = this.editMode
			? api("admin/editTimeAccountInitialization.json").post({...data, timeAccountId: this.editTimeAccountId})
			: api("admin/initializeTimeAccount.json").post(data);
		request
			.done(() => {
				FormUtil.removeAllFeedback(form);

				Button.resetLoading(button);
				modal.close();

				$(this).triggerHandler(TimeAccountInitializationDialog.EVENT_INITIALIZED);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markWizardFieldErrors(form, response.responseJSON.fieldErrors);
				}
				Button.resetLoading(button);
			});
	}

	private updateTitle() {
		let messageKey: string;
		if (this.editMode) {
			messageKey = `user.crud.timeAccount.initialization.edit.${this.selectedTimeAccountEntryType}`;
		} else if (this.command?.timeAccountEntryType === TimeAccountEntryType.ReEntry) {
			messageKey = "user.crud.timeAccount.initialization.reEntry";
		} else if (this.command && this.command.vacationAccount && !this.command.workingTimeTracking) {
			messageKey = "user.crud.timeAccount.initialization.section.startbalance";
		} else {
			messageKey = "user.crud.timeAccount.initialization";
		}
		this.modal.setTitle(messages.withArguments(messageKey, this.username));
	}

	private initDeductFromAccountSelects(userOrder?: OvertimeDeductionAccountType[]) {
		// Get all time account selects
		const selects: JQuery[] = [];
		$('[name="initializeOvertimeDeductionAccountOrder"]').each((i, e) => {
			selects.push($(e));
		});

		// Set order defined by user
		this.customOvertimeDeductionAccountOrder = fillOvertimeDeductionAccountOrder(selects, userOrder ?? [OvertimeDeductionAccountType.OVERTIME_ALLOWANCE_ACCOUNT, OvertimeDeductionAccountType.TIME_ACCOUNT]);

		selects.forEach((select) => select.on("change", () =>
			this.customOvertimeDeductionAccountOrder = fillOvertimeDeductionAccountOrder(
				selects,
				selects.map((e) => $(e).val() as OvertimeDeductionAccountType)
			)
		));
	}
}

interface TimeAccountInitializationCommand {
	timeAccountEntryType: TimeAccountEntryType;
	userId: number;
	trustBasedWorkingTime: boolean;
	workingTimeTracking: boolean;
	vacationAccount: boolean;
	workScheduleModelId: number;
	workScheduleModelAlternatingStartWeek: number | null;
	overtimeDurationIncluded: number;
	overtimeAllowanceAllIn: boolean;
	allowClearNegativeWorkDurationBalance: boolean;
	allInSetWorkDurationBalanceZero: boolean;
	overtimeDeductionAccountOrder: OvertimeDeductionAccountType[];
	vacationYearStart: string;
	initializationDate: string;
	initializationWorkDuration: number;
	initializationVacation: number;
	vacationUnit: VacationUnit;
	vacationDaysPerYear: number;
	holidayCalendarId: number;
	timeZone: string;
}
